<template>
  <AppLayout>
    <div class="add-member-view">
      <div class="header">
        <h1>Add New Member</h1>
      </div>

      <form @submit.prevent="handleSubmit" class="add-member-form">
        <div class="form-group">
          <label for="user">Select User</label>
          <input
            id="user"
            v-model="searchQuery"
            @input="handleSearch"
            type="text"
            placeholder="Search for user by name or email"
            :disabled="isLoading"
          />
          <ul v-if="searchResults.length" class="search-results">
            <li 
              v-for="user in searchResults" 
              :key="user.id" 
              @click="selectUser(user)"
            >
              {{ user.name }} ({{ user.email }})
            </li>
          </ul>
        </div>

        <div v-if="selectedUser" class="selected-user">
          <p>Selected User: <strong>{{ selectedUser.name }}</strong></p>
        </div>

        <div class="form-group">
          <label for="role">Role</label>
          <select id="role" v-model="formData.role" :disabled="isLoading">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div class="form-actions">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="isLoading">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isLoading || !selectedUser">
            {{ isLoading ? 'Adding...' : 'Add Member' }}
          </button>
        </div>
      </form>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useGroupsStore } from '../../stores/groups';
import { storeToRefs } from 'pinia';
import AppLayout from '../../components/layout/AppLayout.vue';
import type { AddMemberData } from '../../types/group';
import { debounce } from 'lodash-es';

// Mock User type for search
interface UserSearchResult {
  id: number;
  name: string;
  email: string;
}

const router = useRouter();
const route = useRoute();
const groupsStore = useGroupsStore();
const { isLoading, error } = storeToRefs(groupsStore);

const formData = ref<AddMemberData>({
  user_id: 0,
  role: 'member'
});

const searchQuery = ref('');
const searchResults = ref<UserSearchResult[]>([]);
const selectedUser = ref<UserSearchResult | null>(null);

const groupId = computed(() => parseInt(route.params.id as string));

// Mock search function - in real implementation this would call a user search API
const handleSearch = debounce(async () => {
  if (searchQuery.value.length < 2) {
    searchResults.value = [];
    return;
  }
  
  console.log(`Searching for: ${searchQuery.value}`);
  // Mock API call
  searchResults.value = [
    { id: 1, name: 'Alice Smith', email: 'alice@example.com' },
    { id: 2, name: 'Bob Johnson', email: 'bob@example.com' },
  ].filter(u => 
    u.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
}, 300);

const selectUser = (user: UserSearchResult) => {
  selectedUser.value = user;
  formData.value.user_id = user.id;
  searchQuery.value = '';
  searchResults.value = [];
};

const handleSubmit = async () => {
  if (!formData.value.user_id) {
    groupsStore.setError('Please select a user to add.');
    return;
  }
  
  try {
    await groupsStore.addMember(groupId.value, formData.value);
    router.push(`/groups/${groupId.value}/members`);
  } catch (err) {
    console.error('Failed to add member:', err);
  }
};

const cancel = () => {
  router.push(`/groups/${groupId.value}/members`);
};
</script>

<style scoped>
.add-member-view {
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.header {
  margin-bottom: 2rem;
}

.header h1 {
  margin: 0;
}

.add-member-form {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 0;
  z-index: 10;
}

.search-results li {
  padding: 0.75rem;
  cursor: pointer;
}

.search-results li:hover {
  background: #f0f0f0;
}

.selected-user {
  background: #e0f7fa;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.error-message {
  color: red;
  margin-bottom: 1rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn:disabled {
  opacity: 0.6;
}

.btn-primary {
  background: blue;
  color: white;
}

.btn-secondary {
  background: grey;
  color: white;
}
</style>
